import { useState, useEffect, useMemo } from 'react';
import { Car, CalendarDays, ArrowLeft } from 'lucide-react';
import { TlacitkoPlochy } from './PlochaUI';
import { REZERVA_LISTY } from '../../components/MobilniLista';
import { useShiftData } from './hooks/useShiftData';
import { deriveShiftState } from './hooks/useShiftState';
import { useRoundedClockIn } from './hooks/useShiftTimer';
import PhaseModal from './PhaseModal';
import ClockInManualModal from './ClockInManualModal';
import TeamMessageSheet from './TeamMessageSheet';
import KartaKolegy from '../../components/KartaKolegy';
import ShiftCardPre from './ShiftCardPre';
import ShiftCardStarting from './ShiftCardStarting';
import ShiftCardActive from './ShiftCardActive';
import PohybSheet from './PohybSheet';
import TankovaniSheet from './TankovaniSheet';
import NakupSheet from './NakupSheet';
import SlotPickerSheet from './SlotPickerSheet';
import MonthOverview from './MonthOverview';
import Volno from './Volno';
import PracovniPlocha from './PracovniPlocha';
import VyuctovaniSheet from './VyuctovaniSheet';
import UzaverkaHlavniSheet from './UzaverkaHlavniSheet';
import UzaverSmenyFinal from './UzaverSmenyFinal';
import ManagerPinDialog from './ManagerPinDialog';
import ZaVolantemPanel from './ZaVolantemPanel';
import MojePokladnaPanel from './MojePokladnaPanel';
import UctenkyHistorieSheet from './UctenkyHistorieSheet';
import KolegovePanel from './KolegovePanel';
import UkolyManazera from './UkolyManazera';
import UkolyProuzek from './UkolyProuzek';


// `kalendar` — měsíční přehled pod kartou směny. Přehled ho na mobilu vypíná
// (kalendář má vlastní sekci „Můj měsíc") a místo prázdného stavu bez směny
// nabídne `onOtevritKalendar`.
export default function MojeSmena({ user, cardStyle, isDark, fillContainer = false, onNavigate, kalendar = true, onOtevritKalendar }) {
    // forceNextShift = po kliknutí na X v post-completion placeholderu user
    // explicitně zavřel info o dokončené smene a chce vidět příští naplánovanou.
    // Persistujeme do localStorage s dnešním datem, ať příští den se sám resetuje.
    const [forceNextShift, setForceNextShiftState] = useState(() => {
        try {
            const raw = localStorage.getItem('mojesmena.forceNextShift');
            if (!raw) return false;
            const parsed = JSON.parse(raw);
            const dnes = new Date().toISOString().slice(0, 10);
            return parsed?.date === dnes;
        } catch { return false; }
    });
    const setForceNextShift = (val) => {
        setForceNextShiftState(val);
        try {
            if (val) {
                const dnes = new Date().toISOString().slice(0, 10);
                localStorage.setItem('mojesmena.forceNextShift', JSON.stringify({ date: dnes }));
            } else {
                localStorage.removeItem('mojesmena.forceNextShift');
            }
        } catch { /* fallback v paměti */ }
    };
    // Co sis dnes vydělal — přebírá se z odpovědi na odhlášení a ukazuje na
    // obrazovce „Dnes máš odpracováno". Drží se v localStorage do konce dne,
    // aby reload po odhlášení souhrn nesmazal.
    const [souhrnSmeny, setSouhrnSmenyState] = useState(() => {
        try {
            const raw = localStorage.getItem('mojesmena.souhrnSmeny');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed?.date === new Date().toISOString().slice(0, 10) ? parsed.souhrn : null;
        } catch { return null; }
    });
    const setSouhrnSmeny = (souhrn) => {
        setSouhrnSmenyState(souhrn);
        try {
            if (souhrn) {
                const dnes = new Date().toISOString().slice(0, 10);
                localStorage.setItem('mojesmena.souhrnSmeny', JSON.stringify({ date: dnes, souhrn }));
            } else {
                localStorage.removeItem('mojesmena.souhrnSmeny');
            }
        } catch { /* fallback v paměti */ }
    };

    const { smena, dochazka, pokladnaSmena, tym, typUvazku, jeDnes, ukoly, loading, refetch, zahajitPripravu, zrusitPripravu, clockIn, vratitAuto, prevzitAuto, vymenitAuto, sendMessage, saveOdpoved, dokoncitFazi, vratitDoPrubehu, potvrditVyuctovani } = useShiftData({ forceNext: forceNextShift });
    // Plocha dlaždice pod kartou: 'ridic' (Já za volantem) | 'pokladna'
    // (Moje pokladna) | 'kolegove' | null (dávka / kontroly). Nahradila sheety
    // auta, pokladny a týmu, které se vysouvaly přes celý cockpit.
    const [plocha, setPlocha] = useState(null);
    const [uctenkyHistorieOpen, setUctenkyHistorieOpen] = useState(false);
    // Plocha pokladny si po pohybu v pokladně (účtenka, vklad…) načte data znovu.
    const [pokladnaObnova, setPokladnaObnova] = useState(0);
    const [nakupOpen, setNakupOpen] = useState(false);

    const [pohybModalOpen, setPohybModalOpen] = useState(null); // null | 'vklad' | 'vydej'
    const [ukoncovaniBusy, setUkoncovaniBusy] = useState(false);
    const [vymenaAutoOpen, setVymenaAutoOpen] = useState(false);
    const [vymenaBusy, setVymenaBusy] = useState(false);
    const [vymenaError, setVymenaError] = useState(null);
    const [endTimeOverride, setEndTimeOverride] = useState(null); // HH:MM pro odhlášení v jiný čas
    const [endTimeEditOpen, setEndTimeEditOpen] = useState(false);
    const [vyuctovaniSheetOpen, setVyuctovaniSheetOpen] = useState(false);
    const [korekceSheetOpen, setKorekceSheetOpen] = useState(false);
    const [uzaverSmenyOpen, setUzaverSmenyOpen] = useState(false);
    const [tankovaniOpen, setTankovaniOpen] = useState(false);
    const [novyVozidloId, setNovyVozidloId] = useState('');
    const [prevzetiBusy, setPrevzetiBusy] = useState(false);
    const [prevzetiError, setPrevzetiError] = useState(null);
    const [clockBusy, setClockBusy] = useState(false);
    const [clockInEditOpen, setClockInEditOpen] = useState(false);
    // Late-start PIN gate: backend vrací 409 + requires_manager_pin když je
    // zpoždění >= tolerance. Zde držíme všechny argumenty původního clockIn,
    // abychom je po PIN ověření mohli znovu poslat (tentokrát s manazer_pin).
    const [pinGate, setPinGate] = useState(null); // { args: { clockManual, vozidloId, pokladnaId }, info: { zpozdeni_min, planovany_start } }
    const [retroToast, setRetroToast] = useState(null); // { jizdy, pohyby, schvalil }
    const [vozidla, setVozidla] = useState(null); // null = neznáno, [] = žádná, [...] = list
    const [pokladny, setPokladny] = useState(null);
    const [vybranyVozidloId, setVybranyVozidloId] = useState('');
    const [vybranaPokladnaId, setVybranaPokladnaId] = useState('');
    const [manualTimeOverride, setManualTimeOverride] = useState(null); // HH:MM když uživatel zadal vlastní čas

    // Persist manualTimeOverride per-smena přes localStorage. Bez tohohle ho
    // ztratíme při Vite HMR re-mountu nebo při manuálním F5 — backend zvolený
    // čas zatím neukládá (lazy storage stačí). Klíč obsahuje smena.id, aby se
    // dvě paralelní směny nepřepisovaly.
    useEffect(() => {
        if (!smena?.id) return;
        const stored = localStorage.getItem(`mojesmena.manualTime.${smena.id}`);
        if (stored && stored !== manualTimeOverride) setManualTimeOverride(stored);
    }, [smena?.id]);
    useEffect(() => {
        if (!smena?.id) return;
        const key = `mojesmena.manualTime.${smena.id}`;
        if (manualTimeOverride) localStorage.setItem(key, manualTimeOverride);
        else localStorage.removeItem(key);
    }, [smena?.id, manualTimeOverride]);
    const [autoPickerOpen, setAutoPickerOpen] = useState(false);
    const [pokladnaPickerOpen, setPokladnaPickerOpen] = useState(false);
    const [clockError, setClockError] = useState(null);
    const [activePhase, setActivePhase] = useState(null);
    const [messageOpen, setMessageOpen] = useState(false);
    const [messageTo, setMessageTo] = useState(null);
    const [activeMember, setActiveMember] = useState(null);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Načti dostupná auta — pre-clock-in (žádné auto ještě nezvolené) i mid-shift
    // (auto bylo vráceno, řidič si musí vzít další). Mid-shift: vozidlo objekt
    // sice ještě může být v response, ale `vozidlo_vraceno_at` je set.
    const potrebujePicker = !!smena?.pozaduje_vozidlo && (!smena?.vozidlo || !!smena?.vozidlo_vraceno_at);
    useEffect(() => {
        if (!smena?.id || !potrebujePicker) return;
        fetch(`/api/me/smena/${smena.id}/dostupna-vozidla`)
            .then(r => r.ok ? r.json() : [])
            .then(setVozidla)
            .catch(() => setVozidla([]));
    }, [smena?.id, potrebujePicker]);

    const state = useMemo(() => deriveShiftState(smena, dochazka), [smena, dochazka]);

    // Karta vyrovnání musí naskočit **sama**, ne až po ručním refreshi —
    // kolega u pultu čeká, až mu manažer potvrdí peníze, a nemá jak říct
    // „zkus to načíst znovu". Dokud se posílala notifikace, refresh spustil
    // `.NovaNotifikace` v app.jsx; po jejím zrušení tu nezbylo nic, co by
    // příchod úkolu ohlásilo. Reverb event existoval celou dobu, jen ho nikdo
    // neposlouchal.
    useEffect(() => {
        if (!user?.id) return;
        let kanal = null;
        const naObnovu = () => refetch();

        // `window.Echo` zakládá app.jsx ve svém efektu, jenže efekty dětí běží
        // dřív než rodičovy — při prvním mountu tu ještě není. Proto se na něj
        // chvíli čeká, místo aby se listener tiše zahodil.
        const pripoj = () => {
            if (!window.Echo) return false;
            kanal = window.Echo.private(`user.${user.id}`);
            kanal.listen('.UzaverkaOdeslana', naObnovu);
            // A opačný směr: manažer potvrdil vyúčtování. Spolu se schválením
            // mohl opravit i docházku, takže kolega musí přetáhnout čerstvá
            // čísla — zvonek s notifikací obrazovku nepřekresluje.
            kanal.listen('.VyuctovaniSchvaleno', naObnovu);
            // Kolega bez pokladny si řekl o zálohu — karta má naskočit taky.
            kanal.listen('.UkolManazeraCeka', naObnovu);
            return true;
        };
        const iv = pripoj() ? null : setInterval(() => { if (pripoj()) clearInterval(iv); }, 300);

        return () => {
            if (iv) clearInterval(iv);
            // `leave()` schválně ne — kanál `user.{id}` drží i app.jsx pro chat
            // a notifikace; odhlášení kanálu by je utnulo taky.
            kanal?.stopListening('.UzaverkaOdeslana', naObnovu);
            kanal?.stopListening('.VyuctovaniSchvaleno', naObnovu);
            kanal?.stopListening('.UkolManazeraCeka', naObnovu);
        };
    }, [user?.id, refetch]);

    // Pojistka pro případ, že WebSocket neběží (typicky pár vteřin po deploji).
    // Bez ní by manažer o čekajícím vyrovnání nevěděl, dokud si stránku
    // nenačte ručně — a kolega u pultu by mezitím čekal, až ho někdo pustí
    // domů. Jen na manažerské směně a jen dokud je přihlášený.
    useEffect(() => {
        if (!smena?.manazer_smeny || !dochazka?.clock_in || dochazka?.clock_out) return;
        const iv = setInterval(() => refetch(), 45000);
        return () => clearInterval(iv);
    }, [smena?.manazer_smeny, dochazka?.clock_in, dochazka?.clock_out, refetch]);

    // Totéž z druhé strany pultu: kolega odeslal vyúčtování a čeká, až mu
    // manažer potvrdí peníze. Tlačítko „Odhlásit" se mu má objevit hned —
    // odemyká ho event `VyuctovaniSchvaleno`, ale když zrovna neběží
    // WebSocket, stál by u pultu se zamčenou obrazovkou. Proto se během
    // čekání ptá sám, každé 4 s (jen po tu chvíli a jen s viditelnou
    // stránkou), a hned po návratu do aplikace.
    //
    // Hned po odeslání uzávěrky ale cockpit ještě drží starý stav fleku
    // (`otevreno`) — event o odeslání chodí jen manažerům, ne kolegovi. Bez
    // téhle pojistky mu závěrečná obrazovka rovnou nabídla „Odhlásit"
    // a čekání se neukázalo vůbec. Proto se po odeslání čeká hned (a tím
    // se spustí i dotazování); jakmile server stav potvrdí, řídí ho zase on
    // — i kdyby manažer vyúčtování vrátil k opravě.
    const [uzaverkaOdeslana, setUzaverkaOdeslana] = useState(false);
    useEffect(() => {
        if (pokladnaSmena?.stav && pokladnaSmena.stav !== 'otevreno') setUzaverkaOdeslana(false);
    }, [pokladnaSmena?.stav]);
    const pokladnaProZaver = uzaverkaOdeslana && pokladnaSmena?.stav === 'otevreno'
        ? { ...pokladnaSmena, stav: 'ceka_na_schvaleni' }
        : pokladnaSmena;
    // Kolega bez pokladny čeká na zálohu — pozná se z docházky. Příznak
    // `zalohaOdeslana` je totéž co `uzaverkaOdeslana` u pokladny: než server
    // stav potvrdí, drží čekání klient.
    const [zalohaOdeslana, setZalohaOdeslana] = useState(false);
    useEffect(() => {
        if (dochazka?.zaloha_ceka_od || dochazka?.zaloha_schvaleno_at) setZalohaOdeslana(false);
    }, [dochazka?.zaloha_ceka_od, dochazka?.zaloha_schvaleno_at]);
    // Komu se vyplácí záloha z pokladny a nemá co vyúčtovat: dohoda se
    // sazbou a bez pokladny. Server rozhodne definitivně (spočítá částku),
    // tohle jen říká, jestli má „Odhlásit" v kartě rovnou žádat o vyrovnání.
    const [pozadatOVyrovnani, setPozadatOVyrovnani] = useState(false);
    const potrebaVyrovnani = !pokladnaSmena
        && ['DPP', 'DPC'].includes(typUvazku)
        && Number(smena?.sazba_kc_h) > 0
        && !dochazka?.clock_out
        && !dochazka?.zaloha_ceka_od
        && !dochazka?.zaloha_schvaleno_at;

    const cekaNaZalohu = !pokladnaSmena
        && !dochazka?.clock_out
        && !dochazka?.zaloha_schvaleno_at
        && (zalohaOdeslana || !!dochazka?.zaloha_ceka_od);

    const cekaNaManazera = cekaNaZalohu || (!!pokladnaProZaver
        && pokladnaProZaver.typ !== 'bistro_hlavni'
        && pokladnaProZaver.stav === 'ceka_na_schvaleni');

    // Odeslaná uzávěrka rozvozce má jediné místo: stránku „Co si předáte"
    // (čekání na manažera, pak Odhlásit). Po obnovení stránky se ale
    // `uzaverSmenyOpen` ztratil a cockpit ukázal tlačítko v kartě — i s „Jiný
    // čas", který po odeslání nedává smysl. Proto se sama otevře, kdykoli
    // cockpit najde odeslanou uzávěrku a kolega ještě není odhlášený.
    // Zpět se z ní dá (šipka); znovu se otevře až při další změně stavu.
    const uzaverkaOdeslanaNaServeru = (!!pokladnaSmena
        && pokladnaSmena.typ !== 'bistro_hlavni'
        && ['ceka_na_schvaleni', 'uzavreno'].includes(pokladnaSmena.stav))
        // Bez pokladny totéž: čekám na zálohu, nebo ji manažer právě potvrdil.
        || (!pokladnaSmena && !!(dochazka?.zaloha_ceka_od || dochazka?.zaloha_schvaleno_at));
    const naSmene = !!dochazka?.clock_in && !dochazka?.clock_out;
    useEffect(() => {
        if (uzaverkaOdeslanaNaServeru && naSmene) setUzaverSmenyOpen(true);
    }, [uzaverkaOdeslanaNaServeru, naSmene]);
    useEffect(() => {
        if (!cekaNaManazera) return;
        const zeptejSe = () => { if (!document.hidden) refetch(); };
        const iv = setInterval(zeptejSe, 4000);
        document.addEventListener('visibilitychange', zeptejSe);
        return () => {
            clearInterval(iv);
            document.removeEventListener('visibilitychange', zeptejSe);
        };
    }, [cekaNaManazera, refetch]);

    // Po odhlášení zůstáváme na **dnešní** směně. Dřív se tu automaticky
    // přepínalo na příští naplánovanou: člověk zavřel den a obrazovka mu
    // místo potvrzení, že má hotovo, nabídla směnu za tři dny. Přechod na
    // příští zůstává ruční — X v placeholderu dokončené směny.

    // Pokladny + auta načteme když je uživatel ve fázi "starting" (vyplňuje
    // úvodní checklisty / vybírá auto/pokladnu před clock-in).
    useEffect(() => {
        if (!smena?.id || state !== 'starting') return;
        if (smena.pozaduje_vozidlo && vozidla === null) {
            fetch(`/api/me/smena/${smena.id}/dostupna-vozidla`)
                .then(r => r.ok ? r.json() : [])
                .then(setVozidla)
                .catch(() => setVozidla([]));
        }
        if (smena.pozaduje_pokladnu && pokladny === null) {
            fetch(`/api/me/smena/${smena.id}/dostupne-pokladny`)
                .then(r => r.ok ? r.json() : [])
                .then(setPokladny)
                .catch(() => setPokladny([]));
        }
    }, [smena?.id, state, smena?.pozaduje_vozidlo, smena?.pozaduje_pokladnu]);
    const rounded = useRoundedClockIn(!!dochazka?.clock_in);

    // Aktivní fáze = první 'todo' v pořadí zahajeni → prubeh → ukonceni.
    // Editovat lze jen aktuálně aktivní fázi, a jen pokud je směna clock-in (active/end).
    const aktivniFazeId = useMemo(() => {
        if (state !== 'active' && state !== 'end') return null;
        return (smena?.faze || []).find(f => f.stav !== 'done')?.id || null;
    }, [smena, state]);

    // Fáze, ve které člověk *stojí* — pro osu ve stálém pruhu. Plyne přímo
    // ze stavu směny, ne z `faze[].stav`: ten se při clock-inu neaktualizuje,
    // takže zahájení zůstane `todo` i s odklikaným checklistem a osa pak
    // ukazovala „zahájení" člověku, který už dvě hodiny jezdil.
    //
    //   starting = vyplňuje úvodní checklist  → zahájení
    //   active   = odbavil ho a jede          → průběh
    //   end      = klikl „Ukončit směnu"      → ukončení
    //
    // Před zahájením neběží nic, takže osa nemá aktivní segment.
    const fazeVOse = useMemo(() => ({
        starting: 'zahajeni',
        active: 'prubeh',
        end: 'ukonceni',
    }[state] ?? null), [state]);

    // Po každé změně fáze v datech (toggle položky) re-fetchnout aktivní fázi pro modal
    useEffect(() => {
        if (!activePhase || !smena?.faze) return;
        const updated = smena.faze.find(f => f.id === activePhase.id);
        if (updated && updated !== activePhase) setActivePhase(updated);
    }, [smena, activePhase]);

    // Dokončená směna nemá v cockpitu vlastní obrazovku — potvrzením, že je
    // hotovo, je `Volno` s `poSmene` („Dnes máš odpracováno" + příští směna
    // jako řádek). Přepnout na něj musí tenhle efekt. Commit 895e30fd ho
    // odstranil, ale stráž `smenaDone && !forceNextShift` pod ním nechal —
    // po odhlášení tak zůstal viset loader, který nikdy neskončil, protože
    // `forceNextShift` už nikdo nepřepnul. Příznak drží localStorage do konce
    // dne, takže reload na obrazovce zůstane.
    //
    // Běžná cesta (odhlášení) si příznak nastaví sama v `onSubmitted`, ať se
    // nefetchuje dvakrát. Tohle je záchrana pro ostatní cesty do `done`:
    // auto-clockout, zavření směny někým jiným, vyčištěné localStorage.
    useEffect(() => {
        if (state === 'done' && !forceNextShift) setForceNextShift(true);
    }, [state, forceNextShift]);

    // Plocha dlaždice se zavře, když přestane mít smysl: začala uzávěrka
    // (ta plochu nahrazuje), auto se vrátilo nebo pokladna zmizela.
    const drziAuto = !!smena?.vozidlo && !smena?.vozidlo_vraceno_at;
    useEffect(() => {
        if (vyuctovaniSheetOpen || korekceSheetOpen || uzaverSmenyOpen) setPlocha(null);
    }, [vyuctovaniSheetOpen, korekceSheetOpen, uzaverSmenyOpen]);
    useEffect(() => {
        if ((plocha === 'ridic' && !drziAuto) || (plocha === 'pokladna' && !pokladnaSmena)) setPlocha(null);
    }, [plocha, drziAuto, pokladnaSmena]);

    const prepniPlochu = (kam) => setPlocha(p => (p === kam ? null : kam));
    const pokladnaSeZmenila = () => { refetch(); setPokladnaObnova(n => n + 1); };
    const otevritVymenuAuta = async () => {
        setVymenaError(null);
        // Načti aktuální seznam — current se odfiltruje v render
        try {
            const r = await fetch(`/api/me/smena/${smena.id}/dostupna-vozidla`);
            setVozidla(r.ok ? await r.json() : []);
        } catch { setVozidla([]); }
        setVymenaAutoOpen(true);
    };

    if (loading) {
        return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Načítám…</div>;
    }

    // Dokončenou směnu cockpit nezobrazuje — useEffect výš přepne
    // forceNextShift=true a hook refetchne příští. Než doběhne, drží se
    // loader, ať uživatel nevidí bliknutí starého cockpitu.
    const smenaDone = state === 'done';
    if (smenaDone && !forceNextShift) {
        return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Načítám…</div>;
    }

    // Volno — dnes žádná směna není. Backend v tom případě vrací nejbližší
    // budoucí (`je_dnes: false`) a modul ji dřív vykreslil jako obyčejnou
    // kartu s tlačítkem „Začít v 10:00": obrazovka se jmenuje „Dnešní směna",
    // nabízí ji začít a že dnes nic není, neřekne nikde. Teď se to řekne
    // první a příští směna je pod tím jako řádek, ne jako výzva k akci.
    if (!smena || !jeDnes) {
        return (
            <div style={{
                maxWidth: isMobile ? 'none' : (fillContainer ? '100%' : 'min(560px, 100%)'),
                margin: fillContainer ? 0 : '0 auto', padding: 0,
                display: 'flex', flexDirection: 'column',
                /* Připnutý rámec jako u ostatních stavů — volno nemá hlavičku
                   v barvě fáze (žádná neběží), ale plochu drží stejně. */
                ...(isMobile ? {
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 20,
                    paddingTop: 'var(--hlavicka-mobil)',
                    paddingBottom: REZERVA_LISTY,
                } : fillContainer
                    ? { flex: 1, minHeight: 0, height: '100%' }
                    : { minHeight: 'auto', maxHeight: 'none' }),
            }}>
                <Volno
                    pristiSmena={smena}
                    tym={tym}
                    souhrn={souhrnSmeny}
                    /* Po dokončení dnešní směny to není volno — je odpracováno.
                       Nadpis „Dnes máš volno" by po osmi hodinách za pultem
                       zněl jako výsměch. */
                    poSmene={forceNextShift}
                    onOtevritKalendar={onOtevritKalendar}
                    isDark={isDark}
                />
                {kalendar && (
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <MonthOverview isDark={isDark} fill={isMobile || fillContainer} flat={isMobile} />
                    </div>
                )}
                {/* Úkoly z Komunikace — sražený proužek u lišty, směnu neblokuje */}
                {isMobile && <UkolyProuzek onNavigate={onNavigate} isDark={isDark} style={{ marginTop: 'auto', marginBottom: 8 }} />}
            </div>
        );
    }

    const openMember = (m) => setActiveMember(m);

    // Kolega z týmu → tvar, který čte `KartaKolegy`. Ta karta žije ve Zprávách
    // (adresář kolegů) a umí telefon, SMS i „napsat"; `TeamMemberSheet` uměl
    // totéž o třídu hůř a byl druhou kopií téhož, která by se s ní rozcházela.
    const kolegaProKartu = (m) => m && ({
        id: m.zamestnanec_id,
        jmeno: [m.jmeno, m.prijmeni].filter(Boolean).join(' '),
        avatar_url: m.avatar_url,
        telefon: m.telefon,
        email: m.email ?? null,
        pozice: m.pozice_nazev,
        smena: (m.smena_cas_od || m.smena_cas_do)
            ? { cas_od: m.smena_cas_od, cas_do: m.smena_cas_do }
            : null,
    });
    const openMessage = () => { setMessageTo(null); setMessageOpen(true); };
    const messageFromMember = (m) => { setMessageTo(m); setActiveMember(null); setMessageOpen(true); };
    // Cockpit team chip → bottom sheet. „Napsat všem" a „Zpráva" per member z
    // sheetu jen přesměrují flow na existující TeamMessageSheet.
    const openTeam = () => prepniPlochu('kolegove');
    const handleTeamMessageAll = () => { openMessage(); };
    const handleTeamMessageMember = (m) => { setMessageTo(m); setMessageOpen(true); };
    // Řádek v týmu → karta kolegy. Bez tohohle nikdo `activeMember` nenastavil:
    // jediným vstupem byla hlavička `ShiftHeader`, která zmizela s nedosažitelnou
    // větví, a karta tím osiřela.
    const handleTeamOpenMember = (m) => { setActiveMember(m); };

    const kolegoveNadUzaverkou = plocha === 'kolegove'
        && (uzaverSmenyOpen || vyuctovaniSheetOpen || korekceSheetOpen);

    const kolegovePlocha = (
        <KolegovePanel
            tym={tym}
            onZpravaVsem={handleTeamMessageAll}
            onZprava={handleTeamMessageMember}
            onOtevrit={handleTeamOpenMember}
            isDark={isDark}
        />
    );

    const handlePrevzitAuto = async () => {
        if (!novyVozidloId || prevzetiBusy) return;
        setPrevzetiBusy(true);
        setPrevzetiError(null);
        try {
            const res = await prevzitAuto(novyVozidloId);
            if (res?.ok) {
                setNovyVozidloId('');
            } else {
                setPrevzetiError(res?.error || 'Převzetí selhalo.');
            }
        } finally {
            setPrevzetiBusy(false);
        }
    };

    const showMidShiftPicker = (state === 'active' || state === 'end') && potrebujePicker;

    // Starting je potřeba pokud směna má auto/pokladnu nebo checklisty fáze zahajeni.
    // Bez ničeho by uživatel jen klikl Dokončit — přeskočíme starting a uděláme rovnou clock-in.
    const fazeZahajeni = (smena?.faze || []).find(f => f.id === 'zahajeni') || null;
    const maChecklistyZahajeni = (fazeZahajeni?.checklisty?.length || 0) > 0;
    const startingNeeded = !!(smena?.pozaduje_vozidlo || smena?.pozaduje_pokladnu || maChecklistyZahajeni);

    // Splnění povinných položek všech checklistů fáze zahajeni. NOK kontrola
    // a "mimo limit" číslo vyžadují i vyplněnou `poznamka_akce` (popis akce).
    // Obyčejný výpočet, ne useMemo — Rules of Hooks po conditional returnech.
    // Počítá se, ne jen ano/ne: zamčené tlačítko „Začít směnu" má říct, kolik
    // věcí zbývá, ne jen zšednout.
    const chybiKontrolZahajeni = (() => {
        if (!fazeZahajeni) return 0;
        let n = 0;
        for (const ch of (fazeZahajeni.checklisty || [])) {
            for (const it of (ch.items || [])) {
                if (!it.povinne) continue;
                const odp = it.odpoved || {};
                const isNok = it.typ_odpovedi === 'kontrola' && odp.hodnota_vyber === 'NOK';
                const chybiAkce = (isNok || odp.mimo_limit)
                    && !(odp.poznamka_akce && odp.poznamka_akce.trim());
                if (!it.splneno || chybiAkce) n++;
            }
        }
        return n;
    })();
    const vsechnyPovinneSplneny = chybiKontrolZahajeni === 0;

    const handleClockIn = async (manualTime = null, manazerPin = null) => {
        const vozidloId = vybranyVozidloId || null;
        const pokladnaId = vybranaPokladnaId || null;
        const finalManualTime = manualTime ?? manualTimeOverride ?? null;
        setClockBusy(true);
        setClockError(null);
        try {
            const res = await clockIn(finalManualTime, vozidloId, pokladnaId, manazerPin);
            if (res?.requires_manager_pin && !manazerPin) {
                // Přepustka pro pozdní zahájení — schovat error, otevřít PIN modal
                setPinGate({
                    args: { clockManual: finalManualTime, vozidloId, pokladnaId },
                    info: {
                        zpozdeni_min: res.zpozdeni_min,
                        planovany_start: res.planovany_start,
                        tolerance_min: res.tolerance_min,
                    },
                });
                return { ok: false, requires_manager_pin: true };
            }
            if (!res?.ok) {
                setClockError(res?.error || 'Zahájení směny selhalo.');
                return res;
            }
            setManualTimeOverride(null);
            setPinGate(null);
            const jizdy = res.retro_jizdy_count || 0;
            const pohyby = res.retro_pohyby_count || 0;
            const schvalil = res.pin_pozdni_zahajeni?.schvalil_jmeno;
            if (jizdy || pohyby || schvalil) {
                setRetroToast({ jizdy, pohyby, schvalil });
                setTimeout(() => setRetroToast(null), 8000);
            }
            return res;
        }
        finally {
            setClockBusy(false);
            setClockInEditOpen(false);
        }
    };

    const handlePinGateSubmit = async (pin) => {
        if (!pinGate) return { ok: false };
        const { clockManual, vozidloId, pokladnaId } = pinGate.args;
        const res = await clockIn(clockManual, vozidloId, pokladnaId, pin);
        if (res?.ok) {
            setManualTimeOverride(null);
            setPinGate(null);
            const jizdy = res.retro_jizdy_count || 0;
            const pohyby = res.retro_pohyby_count || 0;
            const schvalil = res.pin_pozdni_zahajeni?.schvalil_jmeno;
            setRetroToast({ jizdy, pohyby, schvalil });
            setTimeout(() => setRetroToast(null), 8000);
        }
        return res;
    };

    // Klik "Začít směnu" v ShiftCardPre — když je co řešit (auto/pokladna/checklisty),
    // backend vytvoří dochazku bez clock_in (lazy-create checklisty) a frontend
    // přejde do `starting` stavu odvozeného z dat. Bez čeho dělat → rovnou clockIn.
    const handlePreClockIn = async () => {
        setClockError(null);
        if (!startingNeeded) {
            handleClockIn();
            return;
        }
        const res = await zahajitPripravu();
        if (!res?.ok) setClockError(res?.error || 'Nepodařilo se zahájit přípravu.');
    };

    const handleZrusitPripravu = async () => {
        setClockError(null);
        setManualTimeOverride(null);
        await zrusitPripravu();
    };

    // Po potvrzení času v ClockInManualModal: pokud chybí auto/pokladna/checklisty,
    // čas si uložíme a zahájíme přípravu (přechod na starting kartu).
    // Jinak rovnou clockIn s tím časem.
    const handleManualTimeConfirm = async (t) => {
        setClockInEditOpen(false);
        if (!startingNeeded) {
            handleClockIn(t);
            return;
        }
        setManualTimeOverride(t);
        const res = await zahajitPripravu();
        if (!res?.ok) setClockError(res?.error || 'Nepodařilo se zahájit přípravu.');
    };

    // Stav `pre` (před zahájením přípravy) nebo `starting` (vyplňování úvodních
    // checklistů + výběr auta/pokladny). Ostatní stavy (active/end/done) padají
    // do hlavního renderu níže.
    if (state === 'pre' || state === 'starting') {
        const isStarting = state === 'starting';
        const vozidloItem = Array.isArray(vozidla)
            ? vozidla.find(v => v.id == vybranyVozidloId) || null
            : null;
        const pokladnaItem = Array.isArray(pokladny)
            ? pokladny.find(p => p.id == vybranaPokladnaId) || null
            : null;

        // Pre/Starting fáze na mobilu = „nativní" fullscreen cockpit: edge-to-edge,
        // bez scrollu, ploché panely. Breakout přes záporné boční okraje —
        // stejný osvědčený pattern jako Prehled tab bar (zruší 16px padding
        // <main>). KLÍČOVÉ: `maxWidth` nesmí být `100%`, jinak ořeže šířku
        // zpět na obsahovou plochu a breakout se jen posune doleva.
        const edgeToEdge = isMobile;
        return (
            <div style={{
                maxWidth: edgeToEdge ? 'none' : (fillContainer ? '100%' : 'min(560px, 100%)'),
                /* fillContainer = flex potomek: `margin:0 auto` (auto příčné okraje)
                   by zrušilo `stretch` a kontejner by se smrštil na obsah → karta
                   užší než okno. Bez fillContaineru se centruje pod `maxWidth`. */
                margin: fillContainer ? 0 : '0 auto', padding: 0,
                boxSizing: 'border-box',
                ...(edgeToEdge ? null : { width: '100%' }),
                display: 'flex', flexDirection: 'column',
                /* Mobil = modul připnutý **od horní hrany displeje**, ne až
                   pod výřezem: hlavička (StalyPruh) si výřez odsadí sama a
                   barva fáze z ní vyrůstá dolů. Odsazení <main>
                   (`--hlavicka-mobil`) by ji drželo o 87 px níž.
                   Rezerva pod lištou patří na rámec, ne dovnitř scrollu —
                   v tomhle modulu se schválně neroluje. */
                ...(isMobile ? {
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 20,
                    paddingBottom: REZERVA_LISTY,
                } : null),
                /* Výšku na mobilu určuje `top: 0` + `bottom: 0` z připnutí
                   výš — dopočet přes `mobilFillHeight` by ji přebil kratší
                   hodnotou a obsah by se zmáčkl. Dopočet zůstává jen pro
                   plochu, kde se modul do stránky vkládá normálně. */
                ...(isMobile ? null
                    : fillContainer ? { flex: 1, minHeight: 0, height: '100%' }
                    : { minHeight: 'auto', maxHeight: 'none' }),
            }}>
                {!isStarting && (
                    <>
                        {/* Horní panel — shift karta. flexShrink: 0 = drží výšku
                            obsahu, žádný scroll. Kalendář dole vyplní zbytek. */}
                        <div style={{ flexShrink: 0 }}>
                            <ShiftCardPre
                                smena={smena}
                                tym={tym}
                                onClockIn={handlePreClockIn}
                                onClockInManual={() => setClockInEditOpen(true)}
                                onOpenTeam={null}
                                onZpravaKolegovi={handleTeamMessageMember}
                                faze={smena.faze || []}
                                clockBusy={clockBusy}
                                isDark={isDark}
                                isMobile={isMobile}
                            />
                        </div>
                        {kalendar && (
                            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <MonthOverview isDark={isDark} fill={isMobile || fillContainer} flat={isMobile} />
                            </div>
                        )}
                    </>
                )}

                {isStarting && (
                    /* Dva pásy jako v běžící směně: stálý pruh s tlačítkem
                       v hraně · checklist zahájení. Checklist jde dovnitř
                       karty, aby vyplnil výšku až k liště. */
                    <ShiftCardStarting
                        smena={smena}
                        dochazka={dochazka}
                        tym={tym}
                        vozidloItem={vozidloItem}
                        pokladnaItem={pokladnaItem}
                        manualTimeOverride={manualTimeOverride}
                        onOpenAutoPicker={() => setAutoPickerOpen(true)}
                        onOpenPokladnaPicker={() => setPokladnaPickerOpen(true)}
                        onDokoncit={() => handleClockIn()}
                        onCancel={handleZrusitPripravu}
                        onOpenTeam={openTeam}
                        plocha={plocha}
                        faze={smena.faze || []}
                        aktivniFazeId={fazeVOse}
                        onOtevritFazi={setActivePhase}
                        canDokoncit={vsechnyPovinneSplneny}
                        chybiKontrol={chybiKontrolZahajeni}
                        clockBusy={clockBusy}
                        error={clockError}
                        isDark={isDark}
                        isMobile={isMobile}
                    >
                        {/* Stejná pracovní plocha jako v průběhu — v zahájení
                            nese jen kontroly (dávka tu ještě není), ale umí
                            i plochu dlaždice Kolegové s návratem. */}
                        <PracovniPlocha
                            faze={fazeZahajeni}
                            onSaveOdpoved={saveOdpoved}
                            onOtevritFazi={() => setActivePhase(fazeZahajeni)}
                            davkaZapnuta={false}
                            plocha={plocha}
                            onZavritPlochu={() => setPlocha(null)}
                            pozastavitNavrat={messageOpen || !!activeMember || autoPickerOpen || pokladnaPickerOpen}
                            obsahPlochy={plocha === 'kolegove' ? kolegovePlocha : null}
                            isDark={isDark}
                            fill={isMobile || fillContainer}
                        />
                    </ShiftCardStarting>
                )}

                {/* Úkoly z Komunikace — sražený proužek u lišty, směnu neblokuje */}
                {isMobile && <UkolyProuzek onNavigate={onNavigate} isDark={isDark} style={{ marginTop: 'auto', marginBottom: 8 }} />}

                {clockError && !isStarting && (
                    <div style={{
                        // Mobil: pod kalendářem u spodní hrany displeje — odsazení od
                        // bočních hran (cockpit je edge-to-edge) a nad home indicator.
                        margin: isMobile ? '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))' : '12px 0 0',
                        padding: '10px 12px', borderRadius: 7, fontSize: 12,
                        background: 'rgba(239,68,68,0.12)', color: '#ef4444',
                        border: '1px solid rgba(239,68,68,0.3)',
                    }}>
                        {clockError}
                    </div>
                )}

                {/* PhaseModal pro non-bool položky checklistů ve starting stavu */}
                {activePhase && (
                    <PhaseModal
                        faze={activePhase}
                        readOnly={false}
                        onClose={() => setActivePhase(null)}
                        onSave={saveOdpoved}
                        onDokoncit={null}
                        isDark={isDark}
                        isMobile={isMobile}
                    />
                )}

                {autoPickerOpen && (
                    <SlotPickerSheet
                        kind="auto"
                        items={Array.isArray(vozidla) ? vozidla : []}
                        selectedId={vybranyVozidloId ? Number(vybranyVozidloId) : null}
                        onSelect={(id) => { setVybranyVozidloId(id); setClockError(null); }}
                        onClose={() => setAutoPickerOpen(false)}
                        isDark={isDark}
                    />
                )}

                {pokladnaPickerOpen && (
                    <SlotPickerSheet
                        kind="pokladna"
                        items={Array.isArray(pokladny) ? pokladny : []}
                        selectedId={vybranaPokladnaId ? Number(vybranaPokladnaId) : null}
                        onSelect={(id) => { setVybranaPokladnaId(id); setClockError(null); }}
                        onClose={() => setPokladnaPickerOpen(false)}
                        isDark={isDark}
                    />
                )}

                {messageOpen && (
                    <TeamMessageSheet tym={tym} initialPrijemce={messageTo} onClose={() => setMessageOpen(false)} onSend={sendMessage} isDark={isDark} isMobile={isMobile} />
                )}

                {activeMember && (
                    <KartaKolegy
                        clovek={kolegaProKartu(activeMember)}
                        onClose={() => setActiveMember(null)}
                        onZprava={() => messageFromMember(activeMember)}
                        isDark={isDark}
                    />
                )}

                {clockInEditOpen && (
                    <ClockInManualModal
                        autoTime={rounded || ''}
                        busy={clockBusy}
                        onClose={() => setClockInEditOpen(false)}
                        onConfirm={handleManualTimeConfirm}
                        isDark={isDark}
                        isMobile={isMobile}
                    />
                )}

                {pinGate && (
                    <ManagerPinDialog
                        title="PIN manažera dnešní směny"
                        subtitle={`Pozdní zahájení (${pinGate.info.zpozdeni_min} min po plánovaném startu ${pinGate.info.planovany_start})`}
                        onClose={() => setPinGate(null)}
                        onSubmit={handlePinGateSubmit}
                        isDark={isDark}
                    />
                )}

                {retroToast && (
                    <div style={{
                        position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                        zIndex: 1200, maxWidth: 480, width: 'calc(100% - 32px)',
                        background: isDark ? 'rgba(20,20,20,0.95)' : 'rgba(255,255,255,0.98)',
                        border: `1px solid ${isDark ? 'rgba(16,185,129,0.4)' : 'rgba(16,185,129,0.5)'}`,
                        borderRadius: 10, padding: '12px 16px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                        color: isDark ? '#fff' : 'var(--text-primary)',
                        fontSize: 13, lineHeight: 1.45,
                    }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#10B981', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                            Směna zahájena
                        </div>
                        {retroToast.schvalil && (
                            <div>Schválil/a: <b>{retroToast.schvalil}</b></div>
                        )}
                        {(retroToast.jizdy > 0 || retroToast.pohyby > 0) && (
                            <div>Zpětně spárováno: {retroToast.jizdy} jízd, {retroToast.pohyby} pokladních pohybů.</div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Stavy 'active' a 'end' — ShiftCardActive + ChecklistPanel aktuální fáze.
    if (state === 'active' || state === 'end') {
        const isEnd = state === 'end';
        const fazePrubeh = (smena.faze || []).find(f => f.id === 'prubeh');
        const fazeUkonceni = (smena.faze || []).find(f => f.id === 'ukonceni');
        const aktualniFaze = isEnd ? fazeUkonceni : fazePrubeh;

        // Kolik povinných položek aktuální fáze ještě chybí. Počítá se, ne
        // jen ano/ne: zamčené tlačítko má říct **co** chybí, ne jen zšednout.
        // Našedlé tlačítko bez vysvětlení se čte jako rozbitá aplikace.
        const chybiKontrol = (() => {
            if (!aktualniFaze) return 0;
            let n = 0;
            for (const ch of (aktualniFaze.checklisty || [])) {
                for (const it of (ch.items || [])) {
                    if (!it.povinne) continue;
                    const odp = it.odpoved || {};
                    const isNok = it.typ_odpovedi === 'kontrola' && odp.hodnota_vyber === 'NOK';
                    // NOK i hodnota mimo limit se počítají za nesplněné, dokud
                    // k nim není popsaná akce — odkliknuté, ale nevyřešené.
                    const chybiAkce = (isNok || odp.mimo_limit)
                        && !(odp.poznamka_akce && odp.poznamka_akce.trim());
                    if (!it.splneno || chybiAkce) n++;
                }
            }
            return n;
        })();
        const povinneSplneny = chybiKontrol === 0;

        // V end fázi navíc — rozvozový flek musí mít odeslané vyúčtování
        // (pokladna_smena.stav !== 'otevreno'), hlavní pokladna provedenou korekci
        // (dochazka.pokladna_korekce_at) NEŽ se může uživatel odhlásit.
        // Pauzy se neřeší — bereme je z plánu jako čas pro výpočty, případnou
        // korekci času uděláme až v posledním kroku (uzaverka smeny).
        const vyuctovaniHotovo = !pokladnaSmena
            ? true
            : pokladnaSmena.typ === 'bistro_hlavni'
                ? !!dochazka?.pokladna_korekce_at
                // Odeslat nestačí — dokud manažer vyúčtování nepotvrdí, není
                // vyřešené manko ani dýško a server clock-out odmítne. Dřív tu
                // stačilo „cokoliv jiného než otevřeno", takže tlačítko svítilo
                // a odhlášení pak spadlo na hlášku ze serveru.
                : pokladnaSmena.stav === 'uzavreno';
        const ctaPovolene = povinneSplneny && (!isEnd || vyuctovaniHotovo);

        const handleCta = async () => {
            if (ukoncovaniBusy) return;
            // End fáze: místo přímého clock-outu otevřeme závěrečnou obrazovku
            // (souhrn hodin/sazba/dýško + manažerský PIN). Vlastní clock-out
            // proběhne až po PINu manažera v `UzaverSmenyFinal`.
            if (isEnd) {
                setUzaverSmenyOpen(true);
                // Bez pokladny a s dohodou si „Odhlásit" rovnou říká
                // o vyrovnání — obrazovka se pak otevře už s čekáním na
                // manažera, ne s tímtéž tlačítkem podruhé.
                if (potrebaVyrovnani) setPozadatOVyrovnani(true);
                return;
            }
            setUkoncovaniBusy(true);
            try {
                // Active → dokončit fázi průběh (přechod do end)
                await dokoncitFazi('prubeh');
            } finally {
                setUkoncovaniBusy(false);
            }
        };

        // Step 1 ukončení — uvolnit vozidlo. Spinner v CTA dokud běží PATCH.
        const handleVratitDoPrubehu = async () => {
            if (ukoncovaniBusy) return;
            setUkoncovaniBusy(true);
            try {
                const r = await vratitDoPrubehu();
                setClockError(r.ok ? null : (r.error || 'Zpět do průběhu to teď nejde.'));
            } finally {
                setUkoncovaniBusy(false);
            }
        };

        const handleVratitAuto = async () => {
            if (ukoncovaniBusy) return;
            setUkoncovaniBusy(true);
            try {
                await vratitAuto();
            } finally {
                setUkoncovaniBusy(false);
            }
        };

        // Mobilní fix-height layout (jako v starting)
        // Edge-to-edge mobile — outer margin karty Active dýchá od hran displeje.
        // Sjednoceno s pre/starting (i v Prehled, kde tab má `overflowY: visible`
        // schválně pro -16 breakout cockpitu).
        const edgeToEdge = isMobile;

        return (
            <div style={{
                maxWidth: edgeToEdge ? 'none' : (fillContainer ? '100%' : 'min(560px, 100%)'),
                /* fillContainer = flex potomek: `margin:0 auto` (auto příčné okraje)
                   by zrušilo `stretch` a kontejner by se smrštil na obsah → karta
                   užší než okno. Bez fillContaineru se centruje pod `maxWidth`. */
                margin: fillContainer ? 0 : '0 auto', padding: 0,
                boxSizing: 'border-box',
                ...(edgeToEdge ? null : { width: '100%' }),
                display: 'flex', flexDirection: 'column',
                /* Mobil = modul připnutý **od horní hrany displeje**, ne až
                   pod výřezem: hlavička (StalyPruh) si výřez odsadí sama a
                   barva fáze z ní vyrůstá dolů. Odsazení <main>
                   (`--hlavicka-mobil`) by ji drželo o 87 px níž.
                   Rezerva pod lištou patří na rámec, ne dovnitř scrollu —
                   v tomhle modulu se schválně neroluje. */
                ...(isMobile ? {
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 20,
                    paddingBottom: REZERVA_LISTY,
                } : null),
                /* Výšku na mobilu určuje `top: 0` + `bottom: 0` z připnutí
                   výš — dopočet přes `mobilFillHeight` by ji přebil kratší
                   hodnotou a obsah by se zmáčkl. Dopočet zůstává jen pro
                   plochu, kde se modul do stránky vkládá normálně. */
                ...(isMobile ? null
                    : fillContainer ? { flex: 1, minHeight: 0, height: '100%' }
                    : { minHeight: 'auto', maxHeight: 'none' }),
            }}>
                {(
                    <>
                        {/* Dva pásy, nic se neroluje: stálý pruh (s tlačítkem
                            v hraně) · pracovní plocha. Pruh drží výšku,
                            plocha bere zbytek (`flex: 1`) a roluje jen uvnitř
                            sebe, když se obsah výjimečně nevejde. */}
                        <ShiftCardActive
                            smena={smena} dochazka={dochazka} tym={tym}
                            pokladnaSmena={pokladnaProZaver}
                            phase={isEnd ? 'ukonceni' : 'prubeh'}
                            canCta={ctaPovolene}
                            checklistDone={povinneSplneny}
                            chybiKontrol={chybiKontrol}
                            endTimeOverride={endTimeOverride}
                            onCtaEditTime={() => setEndTimeEditOpen(true)}
                            onAuto={() => prepniPlochu('ridic')}
                            onPrevzitAuto={otevritVymenuAuta}
                            onPokladna={() => prepniPlochu('pokladna')}
                            plocha={plocha}
                            onCta={handleCta}
                            onUzaverka={() => setVyuctovaniSheetOpen(true)}
                            onKorekce={() => setKorekceSheetOpen(true)}
                            onVratitAuto={handleVratitAuto}
                            onVratitDoPrubehu={handleVratitDoPrubehu}
                            error={isEnd ? clockError : null}
                            onOpenTeam={openTeam}
                            cekaNaVyrovnani={cekaNaZalohu}
                            onOtevritFazi={setActivePhase}
                            aktivniFazeId={fazeVOse}
                            busy={ukoncovaniBusy}
                            uzaverkaBezi={vyuctovaniSheetOpen || korekceSheetOpen || uzaverSmenyOpen}
                            isDark={isDark}
                            isMobile={isMobile}
                        >
                            {/* Kolegové i během uzávěrky a čekání na manažera —
                                kdo se při ní zasekne, potřebuje zavolat nebo
                                napsat. Dřív dlaždice plochu přepnula, ale
                                uzávěrka měla přednost a nestalo se nic.
                                Uzávěrka zůstává namontovaná (jen skrytá), ať
                                se rozepsané nic neztratí. */}
                            {kolegoveNadUzaverkou && (
                                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                                        {kolegovePlocha}
                                    </div>
                                    <div style={{ flexShrink: 0, display: 'flex', padding: '6px 12px 12px' }}>
                                        <TlacitkoPlochy isDark={isDark} Ikona={ArrowLeft} onClick={() => setPlocha(null)}>
                                            {uzaverSmenyOpen ? 'Zpět k vyrovnání' : 'Zpět k uzávěrce'}
                                        </TlacitkoPlochy>
                                    </div>
                                </div>
                            )}
                            {/* Uzávěrka **nahradí** pracovní plochu, nepřekryje
                                obrazovku. Stálý pruh nad ní zůstane, takže je
                                pořád vidět fáze i dlaždice auta a pokladny. */}
                            <div style={{ display: kolegoveNadUzaverkou ? 'none' : 'contents' }}>
                            {uzaverSmenyOpen ? (
                                /* Závěr směny je poslední krok téhož flow jako
                                   uzávěrka, tak stojí na stejném místě: plocha
                                   pod pruhem. Jako celostránkové překrytí se
                                   tvářil jako jiná aplikace a zmizelo z něj,
                                   kde ve směně člověk je. */
                                <UzaverSmenyFinal
                                    inline
                                    smenaId={smena?.id}
                                    pokladnaSmena={pokladnaProZaver}
                                    cekaNaVyrovnani={cekaNaZalohu}
                                    pozadatOVyrovnani={pozadatOVyrovnani}
                                    onCekaNaManazera={() => { setZalohaOdeslana(true); setPozadatOVyrovnani(false); refetch(); }}
                                    proposedClockOut={endTimeOverride || (smena?.cas_do ? String(smena.cas_do).slice(0, 5) : null)}
                                    onCancel={() => setUzaverSmenyOpen(false)}
                                    onSubmitted={(data) => {
                                        setSouhrnSmeny(data || null);
                                        setUzaverSmenyOpen(false);
                                        // Rovnou na stav po směně, ne přes
                                        // refetch dnešní. Ten by vrátil
                                        // dokončenou směnu, stráž by na ni
                                        // nasadila loader a teprve druhý fetch
                                        // by dojel skutečný stav — dvě čekání
                                        // za sebou tam, kde stačí jedno.
                                        //
                                        // U druhé směny v jednom dni je příznak
                                        // nastavený už z té první, takže by se
                                        // stav nezměnil a nefetchlo by se nic;
                                        // tam musí přijít refetch napřímo.
                                        if (forceNextShift) refetch();
                                        else setForceNextShift(true);
                                    }}
                                    isDark={isDark} isMobile={isMobile}
                                />
                            ) : vyuctovaniSheetOpen ? (
                                <VyuctovaniSheet
                                    inline
                                    open
                                    onClose={() => setVyuctovaniSheetOpen(false)}
                                    onSubmitted={() => {
                                        setVyuctovaniSheetOpen(false);
                                        setUzaverSmenyOpen(true);
                                        // Čekat na manažera hned, a stav fleku
                                        // si přitáhnout — viz `uzaverkaOdeslana`.
                                        setUzaverkaOdeslana(true);
                                        refetch();
                                    }}
                                    smenaId={smena.id}
                                    pokladnaSmena={pokladnaSmena}
                                    proposedClockOut={endTimeOverride || (smena?.cas_do ? String(smena.cas_do).slice(0, 5) : null)}
                                    onEndTimeChange={setEndTimeOverride}
                                    isDark={isDark}
                                    isMobile={isMobile}
                                />
                            ) : korekceSheetOpen ? (
                                /* Uzávěrka hlavní pokladny stojí na stejném
                                   místě jako ta rozvozcova — plocha pod pruhem.
                                   Je to tentýž úkon, jen s jinou pokladnou;
                                   jako celostránkový sheet se tvářil jako jiná
                                   část aplikace a schovával osu fází. */
                                <>
                                {/* Šuplík se počítá dřív než kolegové skončí,
                                    takže jejich vyrovnání chodí sem. Bez úkolu
                                    by musel manažer z uzávěrky odejít. */}
                                <UkolyManazera
                                    ukoly={ukoly}
                                    onPotvrdit={potvrditVyuctovani}
                                    isDark={isDark}
                                />
                                <UzaverkaHlavniSheet
                                    inline
                                    open
                                    pocetUkolu={ukoly?.length ?? 0}
                                    onClose={() => setKorekceSheetOpen(false)}
                                    onSaved={() => {
                                        // Po uzávěrce hlavní pokladny rovnou na
                                        // závěrečnou obrazovku — bez mezikroku
                                        // „Odhlásit v X".
                                        setKorekceSheetOpen(false);
                                        setUzaverSmenyOpen(true);
                                        refetch();
                                    }}
                                    smenaId={smena.id}
                                    isDark={isDark}
                                    isMobile={isMobile}
                                />
                                </>
                            ) : (
                                <>
                                    {/* Úkol drží kolegovi odhlášení, takže stojí
                                        nad prací, ne pod ní. Bez čekajícího
                                        vyúčtování se nevykreslí vůbec — stálá
                                        výška obrazovky se tím nemění. */}
                                    <UkolyManazera
                                        ukoly={ukoly}
                                        onPotvrdit={potvrditVyuctovani}
                                        isDark={isDark}
                                    />
                                    <PracovniPlocha
                                        faze={aktualniFaze}
                                        onSaveOdpoved={saveOdpoved}
                                        onOtevritFazi={() => setActivePhase(aktualniFaze)}
                                        onOpenCockpit={() => onNavigate?.('rozvoz-cockpit')}
                                        davkaZapnuta={!isEnd}
                                        zamestnanecId={user?.zamestnanec_id ?? null}
                                        plocha={plocha}
                                        onZavritPlochu={() => setPlocha(null)}
                                        pozastavitNavrat={nakupOpen || !!pohybModalOpen
                                            || tankovaniOpen || vymenaAutoOpen || uctenkyHistorieOpen
                                            || messageOpen || !!activeMember}
                                        obsahPlochy={plocha === 'ridic' ? (
                                            <ZaVolantemPanel
                                                vozidlo={smena.vozidlo}
                                                maPokladnu={!!pokladnaSmena}
                                                onVymenitAuto={otevritVymenuAuta}
                                                onUvolnitAuto={async () => {
                                                    const ok = await vratitAuto();
                                                    if (ok) setPlocha(null);
                                                    else alert('Auto se nepodařilo uvolnit. Zkus to prosím znovu.');
                                                }}
                                                onNatankovat={() => setTankovaniOpen(true)}
                                                isDark={isDark}
                                            />
                                        ) : plocha === 'pokladna' ? (
                                            <MojePokladnaPanel
                                                pokladnaSmena={pokladnaSmena}
                                                obnova={pokladnaObnova}
                                                onVyfotit={() => setNakupOpen(true)}
                                                onVklad={() => setPohybModalOpen('vklad')}
                                                onVydej={() => setPohybModalOpen('vydej')}
                                                onHistorie={() => setUctenkyHistorieOpen(true)}
                                                isDark={isDark}
                                            />
                                        ) : plocha === 'kolegove' ? kolegovePlocha : null}
                                        isDark={isDark}
                                        fill={isMobile || fillContainer}
                                    />
                                </>
                            )}
                            </div>
                        </ShiftCardActive>
                    </>
                )}

                {/* Úkoly z Komunikace — sražený proužek u lišty, směnu neblokuje */}
                {isMobile && <UkolyProuzek onNavigate={onNavigate} isDark={isDark} style={{ marginTop: 'auto', marginBottom: 8 }} />}

                {/* Sheety + modaly */}
                {vymenaAutoOpen && (
                    <SlotPickerSheet
                        kind="auto"
                        items={(Array.isArray(vozidla) ? vozidla : [])
                            .filter(v => !drziAuto || v.id !== smena.vozidlo?.id)}
                        selectedId={null}
                        prohodit={drziAuto}
                        onSelect={async (newId) => {
                            if (!newId || (drziAuto && newId === smena.vozidlo?.id) || vymenaBusy) return;
                            setVymenaBusy(true);
                            try {
                                // S autem: výměna jedním krokem (i prohození s kolegou) —
                                // když selže, auto zůstává. Bez auta: prosté převzetí.
                                const res = drziAuto ? await vymenitAuto(newId) : await prevzitAuto(newId);
                                if (!res?.ok) {
                                    alert(res?.error || 'Nepodařilo se převzít auto.');
                                    return;
                                }
                                if (res.prohozeno) alert(`Auta prohozená — ${res.prohozeno} má teď tvoje původní auto.`);
                            } finally {
                                setVymenaBusy(false);
                            }
                        }}
                        onClose={() => setVymenaAutoOpen(false)}
                        isDark={isDark}
                    />
                )}

                <UctenkyHistorieSheet
                    open={uctenkyHistorieOpen}
                    onClose={() => setUctenkyHistorieOpen(false)}
                    isDark={isDark} isMobile={isMobile}
                />

                <NakupSheet
                    open={nakupOpen}
                    onClose={() => { setNakupOpen(false); setPokladnaObnova(n => n + 1); }}
                    onSaved={() => { pokladnaSeZmenila(); setNakupOpen(false); }}
                    isDark={isDark} isMobile={isMobile}
                />


                {/* Obě uzávěrky i závěr směny se vykreslují v pracovní ploše
                    výš, ne jako overlay — sheet přes celou obrazovku schovával
                    osu fází a bral kontext, kde ve směně člověk je. */}

                <PohybSheet
                    open={!!pohybModalOpen && !!pokladnaSmena}
                    onClose={() => setPohybModalOpen(null)}
                    onSaved={() => { pokladnaSeZmenila(); setPohybModalOpen(null); }}
                    pokladnaSmena={pokladnaSmena}
                    smer={pohybModalOpen === 'vydej' ? 'vydej' : 'vklad'}
                    onVyfotitUctenku={() => { setPohybModalOpen(null); setNakupOpen(true); }}
                    isDark={isDark} isMobile={isMobile}
                />

                {activePhase && (
                    <PhaseModal
                        faze={activePhase}
                        readOnly={false}
                        onClose={() => setActivePhase(null)}
                        onSave={saveOdpoved}
                        onDokoncit={null}
                        isDark={isDark}
                        isMobile={isMobile}
                    />
                )}

                <TankovaniSheet
                    open={tankovaniOpen && !!smena?.vozidlo}
                    onClose={() => setTankovaniOpen(false)}
                    onSaved={() => { refetch(); setTankovaniOpen(false); }}
                    vozidlo={smena.vozidlo}
                    user={user}
                    smenaId={smena.id}
                    isDark={isDark}
                    isMobile={isMobile}
                />

                {messageOpen && (
                    <TeamMessageSheet tym={tym} initialPrijemce={messageTo} onClose={() => setMessageOpen(false)} onSend={sendMessage} isDark={isDark} isMobile={isMobile} />
                )}

                {activeMember && (
                    <KartaKolegy
                        clovek={kolegaProKartu(activeMember)}
                        onClose={() => setActiveMember(null)}
                        onZprava={() => messageFromMember(activeMember)}
                        isDark={isDark}
                    />
                )}

                {/* Modal pro vlastní čas konce směny (jen v end fázi).
                    Pokud uzávěrka ještě neproběhla → po potvrzení se spustí
                    Krok 1 uzávěrky (s už zapamatovaným časem). Jinak rovnou
                    závěrečná obrazovka. */}
                {endTimeEditOpen && (
                    <ClockInManualModal
                        autoTime={(smena.cas_do || '').slice(0, 5) || ''}
                        busy={false}
                        title={vyuctovaniHotovo ? 'Vlastní čas odhlášení' : 'Vlastní čas konce směny'}
                        accent={vyuctovaniHotovo ? '#10B981' : '#FF8A00'}
                        confirmLabelPrefix={vyuctovaniHotovo ? 'Odhlásit v' : 'Uzavřít v'}
                        busyLabel="Otevírám…"
                        onClose={() => setEndTimeEditOpen(false)}
                        onConfirm={(t) => {
                            setEndTimeOverride(t);
                            setEndTimeEditOpen(false);
                            // Uzávěrka hotová → rovnou závěr směny. Jinak spustí
                            // Krok 1 uzávěrky: hlavní pokladna manažera → uzávěrka
                            // hlavní pokladny, jinak uzávěrka rozvozce.
                            if (vyuctovaniHotovo) {
                                setUzaverSmenyOpen(true);
                                // Vlastní čas je taky „odhlašuji se" — bez
                                // pokladny tím letí i žádost o vyrovnání.
                                if (potrebaVyrovnani) setPozadatOVyrovnani(true);
                            }
                            else if (pokladnaSmena?.typ === 'bistro_hlavni') setKorekceSheetOpen(true);
                            else setVyuctovaniSheetOpen(true);
                        }}
                        isDark={isDark}
                        isMobile={isMobile}
                    />
                )}
            </div>
        );
    }

    // Sem se nikdy nedojde: `deriveShiftState` vrací šest stavů a všechny
    // se vrátily výš (volno/hotovo · pre/starting · active/end). Kdyby
    // někdo přidal sedmý, ať je to vidět hned a ne prázdnou stránkou.
    return null;
}
